import { GlobalPermissionsQuery, NewsfeedQuery, useGlobalPermissions } from '@lopo-tech/lopo-api'
import React, { useCallback } from 'react'
import MasonryFeed from 'src/components/feed/layouts/masonry_feed/masonry_feed'
import ItemGroupHeader from 'src/components/item_group_header/item_group_header'
import { ShowNewPostsButton } from 'src/components/newsfeed/show_new_posts_button'
import PostCreateBanner from 'src/components/post/create/create_banner'
import PostCreate from 'src/containers/post/create'
import FeedWrapper from 'src/containers/smart_components/list_wrapper/feed_wrapper'
import { textResources } from 'src/lang/de'
import { useSelectCurrentProfile } from 'src/reducers/user/user_hooks'
import CreateWithSalutation from '../../../components/post/create/create_with_salutation/create_with_salutation'
import CustomCreateItem from '../../../components/post/create/custom_create_item/custom_create_item'
import { getConfigValue } from '../../../utils/config/config'
import { AuthorizationReason, hasAuthorizationReason } from 'src/utils/authorization_reasons'

interface IProps {
  getNextPage: () => void;
  isPostCreateOpen?: boolean;
  loading: boolean;
  newsfeedData?: NewsfeedQuery['newsfeed']
  showNewPostButton?: boolean;
  onVisibleChange: (id: string, inView: boolean) => void
}

const labels = textResources.postCreate

function isPostCreationDisabled (permissionData: GlobalPermissionsQuery): boolean {
  const globalPermissions = permissionData?.globalPermissions

  const canCreateArticleDisabled = hasAuthorizationReason(globalPermissions?.canCreateArticle?.reasons?.details, AuthorizationReason.POSTING_NOT_PERMITTED)
  const canCreateEventDisabled = hasAuthorizationReason(globalPermissions?.canCreateEvent?.reasons?.details, AuthorizationReason.POSTING_NOT_PERMITTED)
  const canCreateSnapshotDisabled = hasAuthorizationReason(globalPermissions?.canCreateSnapshot?.reasons?.details, AuthorizationReason.POSTING_NOT_PERMITTED)

  return canCreateArticleDisabled && canCreateEventDisabled && canCreateSnapshotDisabled
}

const useGetRenderPostCreate = (isPostCreateOpen: boolean) => {
  const { canCreateArticle, canCreateEvent, canCreateSnapshot, data } = useGlobalPermissions()
  const canCreatePost = canCreateArticle || canCreateEvent || canCreateSnapshot
  const currentProfile = useSelectCurrentProfile()

  const displayCustomCreateItem =
    getConfigValue<boolean>('featureFlags.dynamicSalutation') &&
    ['no_user_profile', 'PrivateProfile'].includes(currentProfile?.__typename ?? '')

  return useCallback((): React.ReactNode => {
    if (!data) {
      return null
    }

    const postCreationDisabled = data ? isPostCreationDisabled(data) : false

    return (
      <React.Fragment key="postCreate">
        {postCreationDisabled
          ? (
            <ItemGroupHeader title={labels.featureDisabledHeading} />
          )
          : (
            <>
              <CustomCreateItem>
                <PostCreateBanner
                  callToActionText={labels.createPostTitle}
                  featureBlocked={!canCreatePost}
                  tileSize
                  forceWizard
                  centerText
                  CustomCreateItem={
                    displayCustomCreateItem ? CreateWithSalutation : undefined
                  }
                />
              </CustomCreateItem>
              <PostCreate
                open={canCreatePost && isPostCreateOpen}
                featureBlocked={!canCreatePost}
              />
            </>
          )}
      </React.Fragment>
    )
  }, [
    data,
    canCreatePost,
    displayCustomCreateItem,
    isPostCreateOpen
  ])
}

const NewsFeed: React.FC<IProps> = ({
  getNextPage,
  isPostCreateOpen = false,
  loading,
  newsfeedData,
  showNewPostButton,
  onVisibleChange
}) => {
  const renderPostCreate = useGetRenderPostCreate(isPostCreateOpen)

  return (
    <>
      {showNewPostButton
        ? (
          <ShowNewPostsButton />
        )
        : null}
      <FeedWrapper getNextPage={getNextPage}>
        <MasonryFeed
          items={newsfeedData?.items ?? []}
          renderPostCreate={renderPostCreate}
          isLoading={loading}
          onVisibleChange={onVisibleChange}
        />
      </FeedWrapper>
      {/* <PaginationFooter feedType={FEED_TYPE_NEWS} /> */}
    </>
  )
}

NewsFeed.displayName = 'NewsFeed'

export default NewsFeed
